import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import Swal from 'sweetalert2';
import { PoliticaPrivacidad } from '@core/models/Registration';
import {
  CookiesConsentCategories,
  CookiesConsentService,
  CookiesConsentStatus,
  SaveCookiesConsentPayload,
} from '@core/http/cookies-consent.service';

type ConsentDecision =
  | 'ACEPTAR_TODAS'
  | 'RECHAZAR_NO_NECESARIAS'
  | 'CONFIGURACION_GUARDADA';

@Component({
  selector: 'app-cookies-consent',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './cookies-consent.component.html',
  styleUrl: './cookies-consent.component.scss',
})
export class CookiesConsentComponent implements OnInit {
  visible = false;
  loading = true;
  saving = false;
  showConfigModal = false;
  showPolicyModal = false;

  sessionId = '';
  policy: PoliticaPrivacidad | null = null;
  currentStatus: CookiesConsentStatus | null = null;
  categories: CookiesConsentCategories = this.getDefaultCategories();

  constructor(private cookiesConsentService: CookiesConsentService) {}

  ngOnInit(): void {
    this.sessionId = this.cookiesConsentService.getOrCreateSessionId();
    this.initializeConsentFlow();
  }

  acceptAll(): void {
    void this.saveDecision('ACEPTAR_TODAS', {
      tecnicas: true,
      analiticas: true,
    });
  }

  rejectNonEssential(): void {
    void this.saveDecision('RECHAZAR_NO_NECESARIAS', {
      tecnicas: true,
      analiticas: false,
    });
  }

  openConfiguration(): void {
    if (this.loading || this.saving) {
      return;
    }

    this.showConfigModal = true;
  }

  closeConfiguration(): void {
    if (this.saving) {
      return;
    }

    this.showConfigModal = false;
  }

  openPolicy(): void {
    if (!this.policy) {
      return;
    }

    this.showPolicyModal = true;
  }

  closePolicy(): void {
    this.showPolicyModal = false;
  }

  saveConfiguration(): void {
    void this.saveDecision('CONFIGURACION_GUARDADA', {
      tecnicas: true,
      analiticas: !!this.categories.analiticas,
    });
  }

  retry(): void {
    this.loading = true;
    this.initializeConsentFlow();
  }

  private initializeConsentFlow(): void {
    this.cookiesConsentService.setAnalyticsAllowed(false);

    this.cookiesConsentService.getCurrentConsent(this.sessionId).subscribe({
      next: (status) => {
        this.currentStatus = status;
        this.categories = { ...status.categorias };
        this.cookiesConsentService.setAnalyticsAllowed(!!status.categorias.analiticas);

        if (!status.requiereConsentimiento) {
          this.visible = false;
          this.loading = false;
          this.showConfigModal = false;
          return;
        }

        this.loadPolicy();
      },
      error: () => {
        this.visible = false;
        this.loading = false;
        this.cookiesConsentService.setAnalyticsAllowed(false);
      },
    });
  }

  private loadPolicy(): void {
    this.cookiesConsentService.getCookiePolicy().subscribe({
      next: (policy) => {
        this.policy = policy;
        this.visible = !!policy;
        this.loading = false;
      },
      error: () => {
        this.policy = null;
        this.visible = false;
        this.loading = false;
      },
    });
  }

  private async saveDecision(
    decision: ConsentDecision,
    categories: CookiesConsentCategories
  ): Promise<void> {
    if (!this.policy || this.saving) {
      return;
    }

    const payload: SaveCookiesConsentPayload = {
      idPoliticaPrivacidad: this.policy.idPoliticaPrivacidad,
      versionPolitica: this.policy.edicion,
      canal: 'WEB',
      decision,
      categorias: {
        tecnicas: true,
        analiticas: !!categories.analiticas,
      },
      sessionId: this.sessionId,
      deviceId: null,
    };

    this.saving = true;
    this.cookiesConsentService.setAnalyticsAllowed(false);

    this.cookiesConsentService.saveConsent(payload).subscribe({
      next: (response) => {
        this.saving = false;

        if (!response.guardado) {
          this.handleSaveError();
          return;
        }

        this.currentStatus = response;
        this.categories = { ...response.categorias };
        this.cookiesConsentService.setAnalyticsAllowed(!!response.categorias.analiticas);
        this.visible = false;
        this.showConfigModal = false;
        this.showPolicyModal = false;
      },
      error: async () => {
        this.saving = false;
        this.handleSaveError();
      },
    });
  }

  private async handleSaveError(): Promise<void> {
    this.cookiesConsentService.setAnalyticsAllowed(false);
    await Swal.fire({
      title: 'NO SE PUDO GUARDAR',
      text: 'NO FUE POSIBLE REGISTRAR TU DECISION DE COOKIES. LAS ANALITICAS SEGUIRAN BLOQUEADAS.',
      icon: 'error',
      confirmButtonText: 'ENTENDIDO',
    });
  }

  private getDefaultCategories(): CookiesConsentCategories {
    return {
      tecnicas: true,
      analiticas: false,
    };
  }
}
